import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog.jsx";
import { Button } from "../../components/ui/button.jsx";
import { Input } from "../../components/ui/input.jsx";
import { Textarea } from "../../components/ui/textarea.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar.jsx";
import { cn } from "../../lib/utils.js";
import { Calendar, CheckCircle2, Mail, Phone, Users } from "lucide-react";

const ROLE_OPTIONS = [
  { id: "speaker", label: "Speaker" },
  { id: "listener", label: "Listener" },
  { id: "moderator", label: "Moderator" },
];

export default function VoiceInviteDialog({
  open,
  onOpenChange,
  directory = [],
  callLink,
  onInvite,
}) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [roleMap, setRoleMap] = useState({});
  const [message, setMessage] = useState("");
  const [manualEmails, setManualEmails] = useState("");
  const [scheduleWindow, setScheduleWindow] = useState("");

  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelected([]);
      setRoleMap({});
      setMessage("");
      setManualEmails("");
      setScheduleWindow("");
    }
  }, [open]);

  const filteredDirectory = useMemo(() => {
    if (!search.trim()) return directory;
    const needle = search.trim().toLowerCase();
    return directory.filter((entry) => {
      return [entry.name, entry.email, entry.phone]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [directory, search]);

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  const handleRoleChange = (id, role) => {
    setRoleMap((prev) => ({ ...prev, [id]: role }));
  };

  const handleSubmit = () => {
    const entries = selected.map((id) => directory.find((item) => (item.id || item.email || item.phone) === id)).filter(Boolean);
    const normalized = entries.map((entry) => ({
      id: entry.id || entry.email || entry.phone,
      name: entry.name || entry.email || entry.phone,
      email: entry.email,
      phone: entry.phone,
      role: roleMap[entry.id || entry.email || entry.phone] || "Invite sent",
    }));
    const manualList = manualEmails.split(/[\n,;]+/).map((value) => value.trim()).filter(Boolean);
    onInvite?.({
      participants: normalized,
      manual: manualList,
      message: message.trim() || undefined,
      window: scheduleWindow.trim() || undefined,
      link: callLink,
    });
  };

  const canSubmit = selected.length > 0 || manualEmails.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold leading-tight">Add people to this call</DialogTitle>
          <p className="text-xs text-muted-foreground">Select teammates, set their role, and optionally include a personal message or timing.</p>
        </DialogHeader>
        <div className="grid gap-6 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <div className="space-y-4">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search directory"
              className="text-sm"
            />
            <div className="max-h-72 overflow-auto rounded-2xl border border-border bg-muted/20">
              {filteredDirectory.length ? (
                <ul className="divide-y divide-border/50">
                  {filteredDirectory.map((entry) => {
                    const id = entry.id || entry.email || entry.phone;
                    const active = selected.includes(id);
                    const role = roleMap[id] || "speaker";
                    return (
                      <li key={id} className={cn("flex items-center justify-between gap-3 px-4 py-3", active ? "bg-primary/10" : "")}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                            checked={active}
                            onChange={() => toggleSelect(id)}
                          />
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={`https://i.pravatar.cc/80?u=${encodeURIComponent(entry.email || entry.name || id)}`} alt={entry.name || id} referrerPolicy="no-referrer" />
                            <AvatarFallback>{(entry.name || "M").slice(0, 2).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <span>
                            <span className="block font-semibold text-foreground">{entry.name || entry.email || entry.phone}</span>
                            <span className="text-[11px] text-muted-foreground">{[entry.email, entry.phone].filter(Boolean).join(" • ") || "Directory contact"}</span>
                          </span>
                        </div>
                        <select
                          value={role}
                          onChange={(event) => handleRoleChange(id, event.target.value)}
                          className="rounded-lg border border-border bg-background px-2 py-1 text-xs"
                        >
                          {ROLE_OPTIONS.map((roleOption) => (
                            <option key={roleOption.id} value={roleOption.id}>{roleOption.label}</option>
                          ))}
                        </select>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="p-6 text-center text-xs text-muted-foreground">No contacts found. Try syncing from the Connect page.</div>
              )}
            </div>
            <div className="rounded-2xl border border-border bg-muted/30 p-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-2 font-semibold uppercase tracking-wide">
                <Users className="h-3.5 w-3.5" /> Selected
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {selected.length ? (
                  selected.map((id) => {
                    const entry = directory.find((item) => (item.id || item.email || item.phone) === id);
                    const label = entry?.name || entry?.email || id;
                    const role = roleMap[id] || "speaker";
                    return (
                      <span key={id} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                        {label}
                        <span className="text-[10px] uppercase text-primary/80">{role}</span>
                      </span>
                    );
                  })
                ) : (
                  <span>No participants selected yet.</span>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-2xl border border-border bg-muted/20 p-4 text-sm">
            <div className="rounded-2xl border border-border bg-background/40 p-3 text-xs">
              <div className="flex items-center gap-2 font-semibold uppercase tracking-wide text-muted-foreground">
                <Mail className="h-3.5 w-3.5" /> Message included with invite
              </div>
              <Textarea
                rows={4}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Let participants know what to prepare."
                className="mt-2"
              />
            </div>
            <div className="rounded-2xl border border-border bg-background/40 p-3 text-xs">
              <div className="flex items-center gap-2 font-semibold uppercase tracking-wide text-muted-foreground">
                <Phone className="h-3.5 w-3.5" /> Manual emails or numbers
              </div>
              <Textarea
                rows={3}
                value={manualEmails}
                onChange={(event) => setManualEmails(event.target.value)}
                placeholder="Add emails or numbers separated by commas"
                className="mt-2"
              />
            </div>
            <div className="rounded-2xl border border-border bg-background/40 p-3 text-xs">
              <div className="flex items-center gap-2 font-semibold uppercase tracking-wide text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" /> Time window (optional)
              </div>
              <Input
                value={scheduleWindow}
                onChange={(event) => setScheduleWindow(event.target.value)}
                placeholder="e.g. Today 4:00 – 4:30 PM UTC"
                className="mt-2"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">We’ll include this guidance in the invite email.</p>
            </div>
            <div className="rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-3 text-xs text-primary">
              Link preview: {callLink}
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            <CheckCircle2 className="mr-2 h-4 w-4" /> Send invites
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
